import isOnlineStatus from 'is-online';
import { defer, from, Observable, of, zip } from 'rxjs';
import { map, mergeMap, switchMap, switchMapTo } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { ApplicationError } from '../../_errors/application-error';
import { Inspection, InspectionDetailNew, InspectionDetailUpdate, InspectionForSharedLock, InspectionList, InspectionListItem, InspectionLocation, InspectionPermission, InspectionUpdateLocation, Inspector } from '../../_objects/inspection';
import { InspectionReportList } from '../../_objects/inspection-report';
import { SubjectLocation, SubjectModel } from '../../_objects/subject-model';
import { InspectionSchema, InspectionSchemaLocationUpdate, InspectionSchemaUpdate } from '../../_offline/models/inspection.schema';
import { InspectionImageSchema } from '../../_offline/models/inspectionImage.schema';
import { SubjectSchema } from '../../_offline/models/subject.schema';
import { SubjectModelSchema } from '../../_offline/models/subjectModel.schema';
import { UploadDataType, UploadManagementSchema } from '../../_offline/models/upload-management.schema';
import { GUID } from '../../app.constants';
import { UtilsService } from '../_utils.service';
import { IndividualReportDbService } from '../database/individual-report-db.service';
import { InspectionDbService } from '../database/inspection-db.service';
import { InspectionImageDbService } from '../database/inspection-image-db.service';
import { SubjectDbService } from '../database/subject-db.service';
import { SubjectModelDbService } from '../database/subject-model-db.service';
import { UploadManagementDbService } from '../database/upload-management-db.service';
import { FileSystemAccessService } from '../file-system-access.service';
import { InspectionContractService } from '../interfaces/inspection-contract.service';
import { NetworkStatusService } from '../network-status.service';
import { InspectionOnlineService } from '../online/inspection-online.service';
import { SubjectFilter } from '../storage.service';
import { InspectionReportOfflineService } from './inspection-report-offline.service';
import { LoginUserOfflineService } from './login-user-offline.service';
import { StereoMeasurementImageOfflineService } from './stereo-measurement-offline.service';
import { SubjectModelOfflineService } from './subject-model-offline.service';

export class InspectionItemWrapper extends InspectionListItem {
  inspectorId: string = '';
  reportCount: number = null;
  imageCount: number = null;

  constructor(item: InspectionListItem, inspectorId: string, reportCount: number, imageCount: number) {
    super();
    this.id = item.id;
    this.inspectionDate = item.inspectionDate;
    this.inspectors = item.inspectors;
    this.permission = item.permission;
    this.status = item.status;
    this.subjectModelId = item.subjectModelId;
    this.subjectModelName = item.subjectModelName;
    this.subjectSerialNo = item.subjectSerialNo;
    this.title = item.title;
    this.sharedLockBy = item.sharedLockBy;
    this.isOfflineData = item.isOfflineData;

    this.inspectorId = inspectorId;
    this.reportCount = reportCount;
    this.imageCount = imageCount;
  }
}

@Injectable({
  providedIn: 'root'
})
export class InspectionOfflineService implements InspectionContractService {

  constructor(
    private inspectionDbService: InspectionDbService,
    private inspectionImageDbService: InspectionImageDbService,
    private subjectDbService: SubjectDbService,
    private subjectModelDbService: SubjectModelDbService,
    private loginUserService: LoginUserOfflineService,
    private uploadManagementDbService: UploadManagementDbService,
    private fileSysAccessService: FileSystemAccessService,
    private individualReportDbService: IndividualReportDbService,
    private individualReportOfflineService: InspectionReportOfflineService,
    private networkStatusService: NetworkStatusService,
    private inspectionOnlineService: InspectionOnlineService,
    private utils: UtilsService,
    private subjectModelOfflineService: SubjectModelOfflineService,
    private stereoMeasurementImageOfflineService: StereoMeasurementImageOfflineService
  ) { }

  get tenantId() {
    return this.loginUserService.tenantId;
  }

  get userId() {
    return this.loginUserService.loginUser?.id;
  }

  private get isAppOnline() {
    return this.networkStatusService.isOnline;
  }

  // IS01 GET
  getInspections(statusCode?: string, subjectFilter?: SubjectFilter, isOwn?: boolean,
    sort?: { active: string; direction: string; },
    page?: { index: number; size: number; }): Observable<InspectionList> {

    if (page) {
      if (!page.index) {
        page.index = 1;
      }
      if (!page.size) {
        page.size = 10;
      }
    } else {
      page = { index: 1, size: 10 };
    }
    // 取得範囲の算出
    const start = ((page.index - 1) * page.size);
    const end = (page.index * page.size);

    let inspectionSchemas: InspectionSchema[];
    let subjectModelSchemas: SubjectModelSchema[];
    let subjectSchemas: SubjectSchema[];

    return zip(
      this.inspectionDbService.getListAll(statusCode, subjectFilter, isOwn, sort),
      this.subjectModelDbService.getAll(false, false),
      this.subjectDbService.getAll(subjectFilter?.subjectModelId, false, false)
    ).pipe(switchMap(values => {
      inspectionSchemas = values[0];
      subjectModelSchemas = values[1];
      subjectSchemas = values[2];
      if (inspectionSchemas.length < 1) {
        return zip(
          of([] as InspectionImageSchema[][]),
          of([] as InspectionReportList[])
        );
      }

      const imageSubs = inspectionSchemas.map(insp => this.inspectionImageDbService.getAll(insp.id));
      const reportSubs = inspectionSchemas.map(insp => {
        return this.individualReportOfflineService.getInspectionReportsByInspectionIds([insp.id], 20, 1);
      });

      return zip(
        zip(...imageSubs),
        zip(...reportSubs)
      );
    }))
      .pipe(map(values => {
        const inspectionImagesList = values[0];
        const reportsList = values[1];
        // 検索条件のサブジェクトモデルを抽出
        const subjectModel = subjectFilter?.subjectModelId
          ? subjectModelSchemas.find(sm => sm.id === subjectFilter.subjectModelId)
          : null;

        // サブジェクトの検索条件が指定されている場合、関連サブジェクトを抽出
        const subjects = subjectSchemas.filter(sn => !subjectModel || sn.subjectModelId === subjectModel.id)
          .filter(sn => !subjectFilter?.owner || sn.subjectAttributes.some(_a => _a.owner === subjectFilter?.owner))
          .filter(sn => !subjectFilter?.country || sn.subjectAttributes.some(_a => _a.country === subjectFilter?.country))
          .filter(sn => !subjectFilter?.district || sn.subjectAttributes.some(_a => _a.district === subjectFilter?.district))
          .filter(sn => !subjectFilter?.state || sn.subjectAttributes.some(_a => _a.state === subjectFilter?.state))
          .filter(sn => !subjectFilter?.site || sn.subjectAttributes.some(_a => _a.site === subjectFilter?.site))
          .filter(sn => !subjectFilter?.project || sn.subjectAttributes.some(_a => _a.project === subjectFilter?.project));

        const result = new InspectionList();
        const items = inspectionSchemas
          .filter(insp => !subjects || subjects.some(subject => subject.id === insp.subjectId))
          .map((insp, index) => {
            // 型変換
            const inspSubject = subjectSchemas.find(s => s.id === insp.subjectId);
            const inspSubjectModel = subjectModelSchemas.find(sm => sm.id === inspSubject.subjectModelId);
            const imageList = inspectionImagesList
              .find(images => images && images.length > 0 && images[0].inspectionId === insp.id);
            return this.createInspectionListItem(insp, inspSubjectModel, inspSubject, imageList, reportsList[index]);
          })
          .sort((a, b) => {
            // 配列のソート
            const genreA = a[sort.active].toUpperCase();
            const genreB = b[sort.active].toUpperCase();

            let comparison = 0;
            if (genreA > genreB) {
              comparison = 1;
            } else if (genreA < genreB) {
              comparison = -1;
            }
            return sort.direction.toUpperCase() === 'ASC'
              ? comparison
              : comparison * -1;
          })
          .slice(start, end); // ページ表示分切り出し

        result.items = items;
        result.page = page.index;
        result.total = inspectionSchemas.length;
        return result;
      })
      );

  }

  // IS02 GET
  getInspection(id: string, aliveOnly?: boolean): Observable<Inspection> {
    return this.inspectionDbService.getInspection(id, aliveOnly)
      .pipe(map(schema => {
        return this.createInspection(schema);
      }));
  }

  // IS09 GET
  getCheckedOutInspections(): Observable<Inspection[]> {
    throw new Error('Method cannot be executed in offline mode.');
  }

  // IS03 POST
  addInspection(inspection: InspectionDetailNew): Observable<Inspection> {
    const addSchema = new InspectionSchema();
    const uuid = GUID.generateUUID();

    return this.subjectDbService.getOne(inspection.subjectId)
      .pipe(switchMap(subjectSchema => {
        // get location version
        const subjectModelLocations = subjectSchema.subjectModel.subjectModelLocations;
        const locationVersion = subjectModelLocations.find(_ => _.isNewestVersion).version;
        const subjectAttributes = subjectSchema.subjectAttributes;
        const attributesVersion = subjectAttributes.find(_ => _.isNewestVersion).version;
        addSchema.setInspectionDetailNew(this.tenantId, inspection, true, uuid, this.userId, locationVersion, attributesVersion);
        // offlineモードの時はデフォルトで共有ロック状態にする
        addSchema.sharedLockBy = this.userId;
        return this.inspectionDbService.getListAll();
      }))
      .pipe(switchMap(schemas => {
        const originalTitle = inspection.title;
        let count = 1;
        while (schemas.some(inspectionSchema => inspectionSchema.title === inspection.title)) {
          inspection.title = `${originalTitle} (${count})`;
          count++;
        }
        if (inspection.title.length > 100) {
          throw new Error('Title exceeds 100 characters.');
        }
        addSchema.title = inspection.title;
        return this.inspectionDbService.addInspection(addSchema);
      }))
      .pipe(switchMap(schema => {
        // アップロード管理テーブルに登録
        const uploadManageSchema = new UploadManagementSchema(schema.id, this.tenantId, UploadDataType.Inspection, true, this.userId);
        return this.uploadManagementDbService.put(uploadManageSchema);
      }))
      .pipe(switchMap(schema => this.getInspection(schema.dataId)));
  }

  // IS04 PUT
  updateInspection(inspection: InspectionDetailUpdate): Observable<Inspection> {
    const updateSchema = new InspectionSchemaUpdate();
    let images: InspectionImageSchema[];
    let inspectionSchema: InspectionSchema;
    let oldPath: string;
    let newPath: string;
    let blobPath: string;
    let oldTitle: string;
    let schemas: InspectionSchema[];
    let subjectModel: SubjectModel;

    return zip(
      this.inspectionImageDbService.getAll(inspection.id),
      this.inspectionDbService.getInspection(inspection.id, true),
      from(this.fileSysAccessService.getSaveBlobFolderPath()),
      this.inspectionDbService.getListAll(),
      this.subjectDbService.getOne(inspection.subjectId)
    )
      .pipe(switchMap(values => {
        // get data
        images = values[0];
        inspectionSchema = values[1];
        blobPath = values[2];
        schemas = values[3];
        const subjectSchema = values[4];
        oldTitle = inspectionSchema.title;

        // inspection title rename
        const originalTitle = inspection.title;
        let count = 1;
        while (schemas.some(inspectionSchema => inspectionSchema.title === inspection.title && inspectionSchema.id !== inspection.id)) {
          inspection.title = `${originalTitle} (${count})`;
          count++;
        }
        if (inspection.title.length > 100) {
          throw new Error('Title exceeds 100 characters.');
        }

        oldPath = blobPath + `inspections/${oldTitle}`;
        newPath = blobPath + `inspections/${inspection.title}`;

        const renameOb = oldPath === newPath
          ? of(true)
          : from(this.fileSysAccessService.renameFilePath(oldPath, newPath));

        return zip(
          this.subjectModelOfflineService.getSubjectModel(subjectSchema.subjectModelId, inspection.locationVersion),
          renameOb
        );
      }))
      .pipe(switchMap((values: any) => {
        if (values[1] !== true && !values[1].returnValue) {
          if (values[1].error && values[1].error.toString().includes('Error: EPERM: operation not permitted')) {
            throw new ApplicationError('Error: EPERM: operation not permitted');
          } else {
            throw new ApplicationError('Failed rename inspection folder paths.');
          }
        }

        subjectModel = values[0];

        // update inspection
        updateSchema.setInspectionDetailUpdate(this.tenantId, inspection, this.userId);
        return this.inspectionDbService.updateInspection(updateSchema);
      }))
      .pipe(switchMap(values => {
        inspectionSchema = values;

        if (!this.isAppOnline) {
          return of(values);
        }

        // online sync
        return from(isOnlineStatus()).pipe(switchMap(isNetworkOnline => {
          if (!isNetworkOnline) {
            return of(values);
          }

          return this.inspectionOnlineService.updateInspection(inspection)
            .pipe(switchMap(inspection => {
              const schema = new InspectionSchemaUpdate();
              schema.setInspection(this.tenantId, inspection);
              return this.inspectionDbService.updateInspection(schema);
            }));
        }));
      }))
      .pipe(switchMap(_ => {
        // update image local path
        const termLocations = this.utils.getLocations(subjectModel, null);
        images.forEach(image => {
          const locationName = this.getLocationFolder(termLocations, image.subjectLocationId);
          image.localFilePath = this.getLocalFilePath(inspectionSchema.title, locationName, image.fileName, '');
        });

        return this.inspectionImageDbService.putList(images);
      }))
      .pipe(switchMap(_ => this.getInspection(inspection.id)));
  }

  // IS05 DELETE
  deleteInspection(id: string): Observable<any> {
    return this.inspectionDbService.getInspection(id, false)
      .pipe(
        mergeMap(inspection => {
          const deleteFolder: string = `inspections\\${inspection.title}`;
          const measurementFolder: string = `inspections\\${id}`;

          return zip(
            this.inspectionDbService.deleteInspection(id)
              .pipe(switchMapTo(this.uploadManagementDbService.deleted(id, UploadDataType.Inspection))),
            this.inspectionImageDbService.deleteALL(id)
              .pipe(
                mergeMap(inspectionImageIds => this.stereoMeasurementImageOfflineService.deleteMeasurementIntermediateInfos(id, inspectionImageIds)),
                switchMapTo(this.uploadManagementDbService.deletedByParentId(id, UploadDataType.InspectionImageInfo))
              ),
            from(this.fileSysAccessService.deleteFolder(deleteFolder))
              .pipe(switchMapTo(this.uploadManagementDbService.deletedByParentId(id, UploadDataType.InspectionImageFile)))
              .pipe(switchMapTo(this.uploadManagementDbService.deletedByParentId(id, UploadDataType.InspectionModifiedImageFile)))
              .pipe(switchMapTo(this.uploadManagementDbService.deletedByParentId(id, UploadDataType.InspectionMeasurementImageFile))),
            defer(() => this.fileSysAccessService.deleteFolder(measurementFolder)),
            this.individualReportDbService.deleteALL(id)
          );
        }),
        switchMap(schema => {
          if (!this.isAppOnline) {
            return of(schema);
          }

          return from(isOnlineStatus())
            .pipe(switchMap(isNetworkOnline => {
              if (!isNetworkOnline) {
                return of(schema);
              }

              return this.inspectionOnlineService.deleteInspection(id)
                .pipe(switchMap(() => {
                  return zip(
                    this.inspectionDbService.deleteInspection(id, false),
                    this.inspectionImageDbService.deleteALL(id, false),
                    this.individualReportDbService.deleteALL(id, false),
                    this.uploadManagementDbService.deletedRelatedDataId(id, false)
                  );
                }));
            }));
        })
      );
  }

  // IS07 PUT
  updateInspectionLocations(inspection: InspectionUpdateLocation): Observable<Inspection> {
    const updateSchema = new InspectionSchemaLocationUpdate();
    updateSchema.setInspectionLocation(inspection, this.userId);
    return this.inspectionDbService.updateInspectionLocations(updateSchema)
      .pipe(switchMap(schema => {
        if (!this.isAppOnline) {
          return of(schema);
        }

        return from(isOnlineStatus())
          .pipe(switchMap(isNetworkOnline => {
            if (!isNetworkOnline) {
              return of(schema);
            }

            return this.inspectionOnlineService.updateInspectionLocations(inspection)
              .pipe(switchMap(inspection => {
                const schema = new InspectionSchemaUpdate();
                schema.setInspection(this.tenantId, inspection);
                return this.inspectionDbService.updateInspection(schema);
              }));
          }));
      }))
      .pipe(switchMap(schema => this.getInspection(schema.id)));
  }

  // IS08 PUT
  updateInspectionSharedLock(inspForSharedLock: InspectionForSharedLock): Observable<Inspection> {
    throw new Error('Method cannot be executed in offline mode.');
  }

  private createInspectionListItem(insp: InspectionSchema, subjectModel: SubjectModelSchema,
    sn: SubjectSchema, images: InspectionImageSchema[], reportList: InspectionReportList) {
    const item = new InspectionListItem();
    item.id = insp.id;
    item.title = insp.title;
    item.subjectModelName = subjectModel.name;
    item.subjectSerialNo = sn.serialNo;
    item.inspectionDate = insp.inspectionDate;
    item.status = insp.status;
    item.inspectors = JSON.parse(insp.inspectors) as Inspector[];
    item.permission = this.getPermission(insp);
    item.sharedLockBy = insp.sharedLockBy;
    item.isOfflineData = insp.isOfflineData;

    const result = new InspectionItemWrapper(item, item.id, reportList.items.length, images?.length);

    return result;
  }

  private createInspection(insp: InspectionSchema) {
    const result = new Inspection();
    result.id = insp.id;
    result.title = insp.title;
    result.subjectModelId = insp?.subject?.subjectModel.id;
    result.subjectId = insp?.subject.id;
    result.subjectSerialNo = insp?.subject.serialNo;
    result.inspectionDate = insp.inspectionDate;
    result.status = insp.status;
    result.inspectors = JSON.parse(insp.inspectors) as Inspector[];
    result.isValid = insp.isValid;
    result.isDeleted = insp.isDeletedFlag;
    result.permission = this.getPermission(insp);
    result.createdBy = insp.createdBy;
    result.createDate = insp.createDate;
    result.lastUpdatedBy = insp.lastUpdatedBy;
    result.lastUpdateDate = insp.lastUpdateDate;
    result.inspectionLocations = JSON.parse(insp.inspectionLocations) as InspectionLocation[];
    result.sharedLockBy = insp.sharedLockBy;
    result.schemaVersion = insp.schemaVersion;
    result.locationVersion = insp.locationVersion;
    result.attributesVersion = insp.attributesVersion;

    return result;
  }

  private getPermission(insp: InspectionSchema) {
    let permission = InspectionPermission.Read;

    if (this.loginUserService.isAdminRole()
      || this.loginUserService.loginUser.id === insp.createdBy) {
      permission += InspectionPermission.Write;
    }

    return permission;
  }

  private getLocalFilePath(inspectionTitle: string, folderName: string, fileName: string, suffix: string): string {
    return `inspections/${inspectionTitle}/files/${folderName}/${suffix + fileName}`;
  }

  /**
   * IdentifierからLocationFileName({{ファイル番号}} + "_" + {{Identifier}})を取得
   * @param locations
   * @param locationIdentifier
   * @returns LocationFileName({{ファイル番号}} + "_" + {{Identifier}})
   */
  private getLocationFolder(locations: SubjectLocation[], locationIdentifier: string): string | null {
    for (const location of locations) {
      if (location.identifier === locationIdentifier) {
        return location.folderName;
      } else if (location.children.length > 0) {
        const childResult = this.getLocationFolder(location.children, locationIdentifier);
        if (childResult !== 'Unallocated') {
          return childResult;
        }
      }
    }
    return 'Unallocated';
  }
}
